import React, { useEffect, useRef, useState } from 'react';
import { useGlobalState } from '../stateProvider/StateProvider';
import Page from '../layout/Page';
import Board from '../dashboard/Board';
import Button, { BorderLessButton } from '../forms/Button';
import Title from '../forms/Title';
import styled from 'styled-components';
import Row from '../layout/Row';
import Tile from '../dashboard/Tile';
import Form from '../forms/Form';
import Input from '../forms/Input';
import Heading from '../forms/Heading';
import { widgetIndex, defaultWidgets } from '../widgets/WidgetIndex';
import Checkbox from '../forms/Checkbox';
import useDebounce from '../utility/debounce';
import HamQuest from '../api/HamQuestGiver';
import { QuestRoute } from '../api/QuestRoutes';
import RoleProtected, { userRoles } from '../RoleProtected';
import Select from '../forms/Select';
import ModalFactory from '../Modal';
import { generateId, shallowCompare } from '../utility/functions';
import Banner from '../dashboard/Banner';
import { SCREEN_MAX_WIDTH, SCREEN_WIDTH } from '../../constants/config';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: calc(100svh - 4em);
    color: ${({ theme }) => theme.text};
`;

const DashWrapper = styled.div`
    height: calc(100svh - 8em);
    width: 100%;
    overflow: auto;
    overflow-x: hidden;
`;

const Relative = styled.div`
    position: relative;
`;

const MobileWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
    width: calc(100% - 1.2em);
    // min-height: 50vmin;

    padding: 0.5em;

    border-radius: 1rem;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.background};
    box-shadow: 0px 3px 10px ${({ theme }) => theme.shadow};
`;

const DashScroll = styled(Relative)`
    display: flex;
    height: 2.5em;
    width: inherit;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;

    max-width: 60svw;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        max-width: 100svw;
    }
`;

const DashRow = styled(Row)`
    width: 100%;
    display: flex;
    justify-content: space-between;
    height: 3em;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5em;
        height: auto;
    }
`;

const DashButton = styled(Button)`
    flex: 1;
    white-space: nowrap;

    background: ${({ active, theme }) => active ? theme.button.active : ''};
    color: ${({ active, theme }) => active ? theme.button.activeText : ''};

    &:hover {
        background: ${({ active, theme }) => active ? theme.button.active : ''};
        color: ${({ active, theme }) => active ? theme.button.activeText : ''};
    }
`;


const Home = ({ ...props }) => {
    const {
        state: {
            dashboards
        },
        setDashboard
    } = useGlobalState();

    document.title = 'Hosted Solutions'

    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

    useEffect(() => {
        let keySequence = []
        const handleKeyDown = (event) => {
            const newSequence = [...keySequence, event.key].slice(-konamiCode.length);
            if (newSequence.join('') === konamiCode.join('')) {
                ModalFactory.show({
                    title: '😉',
                    body: <img src='https://www.jimchines.com/wp-content/uploads/2018/05/CleverGirl.gif' alt='Clever you' />,
                    type: 'success',
                    finally: () => (
                        ModalFactory.show({
                            title: '😈',
                            body: <iframe src={Math.random() > 0.5 ? 'https://html-classic.itch.zone/html/9904172/index.html' : 'https://d07riv.github.io/diabloweb/'} width='640' height='480' title='konami' alt='Clever you' />,
                            type: 'error'
                        })
                    )
                })
                keySequence = [];
            }
            keySequence = newSequence;
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const [dashboardLayout, setDashboardLayout] = useState(null);
    const [dashboardIndex, setDashboardIndex] = useState(0);

    const horScroll = useRef();
    const debouncedValue = useDebounce(dashboardLayout, 1000);

    const editDashboard = (dash) => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: 'Edit ' + dash.label,
            icon: 'gauge',
            body: (
                <Form
                    inputs={{ ...dash }}
                    onSubmit={(f) => {
                        setDashboard({
                            index: dashboardIndex,
                            ...f
                        })
                        ModalFactory.close(id);
                    }}
                    text='Update'
                    localForm
                >
                    {({ values, setValue }) => (
                        <>
                            {
                                dashboards.length > 1 &&
                                <Button
                                    onClick={() => {
                                        ModalFactory.confirmation({
                                            id: 'DashRemoveWarning',
                                            type: 'warning',
                                            title: 'Warning',
                                            body: 'Are you sure you want to remove this dashboard?',
                                            closeAfter: true,
                                        })
                                            .then((res) => {
                                                if (res) {
                                                    removeDash(dashboardIndex)
                                                    ModalFactory.close(id, 'DashRemoveWarning')
                                                }
                                            })
                                    }}
                                    label={`Delete ${dash.label}`}
                                />
                            }
                            <Input
                                label={'Dashboard Name'}
                                name='label'
                                autoComplete='off'
                                required
                            />

                            <Heading>Widgets</Heading>
                            <Button
                                label={values.widgets.length === widgetIndex.length ? 'None' : 'All'}
                                onClick={() => setValue('widgets', values.widgets.length === widgetIndex.length ? [] : widgetIndex.map(e => e.indexKey))}
                            />
                            {
                                widgetIndex.map((e, i) => (
                                    <Checkbox
                                        key={`widgets-${i}`}
                                        label={e.label}
                                        name='widgets'
                                        value={e.indexKey}
                                    />
                                ))
                            }
                        </>
                    )}
                </Form>
            ),
        })
    }

    const resetDashboard = () => {
        ModalFactory.confirmation({
            id: 'ResetDashboard',
            type: 'warning',
            title: 'Reset Dashboard',
            body: 'Are you sure you want to reset the dashboard layout? This will remove all customizations.',
            closeAfter: true,
        })
            .then((res) => {
                if (res) {                                        
                    setDashboard({
                        index: dashboardIndex,
                        reset: true
                    });
                }
            });
    };


    const accessLayout = (e) => {
        setDashboardLayout(e)
    }

    const removeDash = () => {
        setDashboard({
            remove: true,
            index: dashboardIndex,
        })
        setDashboardIndex(Math.max(dashboardIndex - 1, 0))
    }

    const updateWidgets = (id) => {
        setDashboard({
            layout: dashboardLayout,
            index: dashboardIndex,
            widgets: dashboards[dashboardIndex].widgets.filter(e => e !== id)
        })
    }

    const dashClick = (i) => {
        setDashboardIndex(i);
    }

    useEffect(() => {
        if (!dashboardLayout) return;
    if (!shallowCompare(dashboardLayout, dashboards[dashboardIndex].layout)) { 
        setDashboard({
                layout: dashboardLayout,
                index: dashboardIndex,
            });  
        }
    }, [dashboardLayout])

    useEffect(() => {
        if (!debouncedValue || SCREEN_WIDTH) return;
        HamQuest.put(QuestRoute.settings.dashboard, { dashboard: dashboards })
            .catch(ex => console.warn(ex))
    }, [debouncedValue])

    return (
        <RoleProtected roles={userRoles.viewDashboard}>
            <Wrapper style={SCREEN_WIDTH ? { overflow: 'auto', gap: '2em' } : null}>
                {/* <Banner /> */}
                <DashRow>
                    <Row>
                        {SCREEN_WIDTH ? (
                            <Select
                                label='Dashboard'
                                value={dashboardIndex}
                                options={dashboards.map((e, i) => ({ label: e.label, value: i }))}
                                onChange={(e) => {
                                    dashClick(e.target.value)
                                }}
                                standalone
                            />
                        ) : (
                            <DashScroll
                                ref={horScroll}
                                onWheel={(e) => horScroll.current.scrollLeft += e.deltaY * 2}
                            >
                                <Row>
                                    {dashboards.map((e, i) => (
                                        <DashButton
                                            key={`dashButton-${i}`}
                                            active={dashboardIndex === i}
                                            onClick={(e) => dashClick(i)}
                                        >
                                            {e.label}
                                        </DashButton>
                                    ))}
                                </Row>
                            </DashScroll>
                        )}

                        {!SCREEN_WIDTH && (
                            <DashButton
                                icon='plus'
                                roles={userRoles.editDashboard}
                                onClick={() => {
                                    setDashboard({
                                        layout: {},
                                        index: dashboards.length,
                                        widgets: defaultWidgets
                                    })
                                    setDashboardIndex(dashboards.length)
                                }}
                                title='Add New Dashboard'
                            />
                        )}
                    </Row>
                    {!SCREEN_WIDTH && (
                        <Row>
                            <BorderLessButton 
                                icon='rotateleft'
                                roles={userRoles.editDashboard}
                                label={'Reset Layout'}
                                onClick={() => resetDashboard(dashboardIndex)}                      
                            />
                            <BorderLessButton
                                icon='cog'
                                label={'Dashboard Settings'}
                                roles={userRoles.editDashboard}
                                onClick={() => editDashboard(dashboards[dashboardIndex])}
                            />                       
                        </Row>
                    )}
                    {SCREEN_WIDTH && (
                        <Row style={{ width: '100%' }}>
                            <DashButton
                                icon='plus'
                                onClick={() => {
                                    setDashboard({
                                        layout: {},
                                        index: dashboards.length,
                                        widgets: defaultWidgets
                                    })
                                    setDashboardIndex(dashboards.length)
                                }}
                                title='Add New Dashboard'
                            />
                            <DashButton
                                icon='rotateleft'
                                roles={userRoles.editDashboard} 
                                onClick={() => resetDashboard(dashboardIndex)}
                                title='Reset Layout'
                            />
                            <DashButton
                                icon='cog'
                                roles={userRoles.editDashboard}
                                onClick={() => editDashboard(dashboards[dashboardIndex])}
                            />
                        </Row>
                    )}
                </DashRow>

                {SCREEN_WIDTH ? (
                    <Page pageBreak>
                        {dashboards[dashboardIndex].widgets.map(e => {
                            const widget = widgetIndex.find(f => f.indexKey === e);
                            return widget ? (
                                <MobileWrap style={{ minHeight: widget.minHeight }}>
                                    <Title>{widget.label}</Title>
                                    {widget.component}
                                </MobileWrap>
                            ) : <span>An Error has occurred.</span>
                        })}
                    </Page>
                ) : (
                    <DashWrapper>
                        <Board
                            existingLayout={{ layout: dashboards[dashboardIndex].layout ?? {}, id: dashboards[dashboardIndex].id }}
                            accessLayout={accessLayout}
                            cells={48}
                        >
                            {dashboards[dashboardIndex].widgets.map((e) => {
                                const widget = widgetIndex.find(f => f.indexKey === e);
                                return widget ? (
                                    <Tile
                                        key={`Tile-${e}`}
                                        tileId={e}
                                        label={widget.label}
                                        onClose={updateWidgets}
                                    >
                                        {widget.component}
                                    </Tile>
                                ) : <span>An Error has occurred.</span>
                            })}
                        </Board>
                    </DashWrapper>
                )}

            </Wrapper>
        </RoleProtected>
    );
}

export default Home;
